import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type Ref,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import type { InputTargetConversation } from "@/api/conversation-targets";
import { useInputTargets } from "@/hooks/use-input-targets";
import { usePickInputTarget } from "@/hooks/use-pick-input-target";
import { conversationDisplayName } from "@/lib/conversation-display";
import { requestStandaloneNewChat } from "@/lib/new-chat-command";
import type { InputTrigger } from "@/lib/input-trigger";
import { InputTargetPickerSurface } from "./input-target-picker-surface";
import { useInputPopoverPlacement } from "./use-input-popover-placement";
import type { InputTargetPickerHandle } from "./use-input-target-picker";
import type { InputBarConversationController } from "./input-bar-types";

const EMPTY_ROWS: InputTargetConversation[] = [];

/** "@" popup above the standalone bar: the "/" popup's surface with recent or
 *  matching chats and sub-agents. Mounted only while open. */
export function InputTargetPickerPopup({
  ref,
  trigger,
  onPicked,
  onBeforePick,
  onNewConversation,
  variant = "default",
  anchorRef,
  popoverScope,
}: {
  ref: Ref<InputTargetPickerHandle>;
  trigger: InputTrigger;
  onPicked: (
    trigger: InputTrigger,
    row?: InputTargetConversation | null,
  ) => void;
  onBeforePick?: () => void;
  onNewConversation?: InputBarConversationController["onNewConversation"];
  variant?: "default" | "standalone";
  anchorRef?: RefObject<HTMLElement | null>;
  popoverScope?: "tray";
}) {
  const popupRef = useRef<HTMLDivElement>(null);
  const tray = popoverScope === "tray";
  const defaultStyle = useInputPopoverPlacement(popupRef, {
    align: "stretch",
    anchorRef,
    strategy: tray ? "fixed" : "absolute",
  });
  const local = onNewConversation !== undefined;
  const targets = useInputTargets({ enabled: !local, search: trigger.query });
  const { pick, pickingId, error } = usePickInputTarget();
  const newPickPendingRef = useRef(false);
  const [pickingNew, setPickingNew] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const query = trigger.query.trim().toLocaleLowerCase();
  // Keep the visible list tied to the typed name even while a newer server
  // search is debouncing. Preserve the server's project/recent order for ties.
  const rows = local
    ? EMPTY_ROWS
    : query
      ? targets.rows
          .filter(
            (row) =>
              conversationDisplayName(row)
                .toLocaleLowerCase()
                .includes(query) ||
              row.task?.ticket_display?.toLocaleLowerCase().includes(query),
          )
          .sort((a, b) => {
            const rank = (row: InputTargetConversation) => {
              const name = conversationDisplayName(row).toLocaleLowerCase();
              return name === query
                ? 0
                : name.startsWith(query)
                  ? 1
                  : name.split(/\s+/).some((word) => word.startsWith(query))
                    ? 2
                    : 3;
            };
            return rank(a) - rank(b);
          })
      : targets.rows;
  // Rows can shrink under a kept highlight before the reset below lands.
  const standalone = variant === "standalone";
  const showNew = (standalone || local) && (!query || "new".startsWith(query));
  const rowCount = rows.length + (showNew ? 1 : 0);
  const selectedIndex = Math.min(highlightedIndex, rowCount - 1);
  const [standalonePlacement, setStandalonePlacement] = useState({
    below: false,
    left: 0,
    width: 300,
  });

  useLayoutEffect(() => {
    if (!standalone) return;
    const root = popupRef.current?.closest("[data-input-bar-root]");
    if (!root) return;
    const measure = () => {
      const rect = root.getBoundingClientRect();
      const popupHeight = popupRef.current?.getBoundingClientRect().height ?? 0;
      const width = Math.min(300, Math.max(0, window.innerWidth - 24));
      const left = Math.max(
        -rect.left,
        Math.min(0, window.innerWidth - 12 - rect.left - width),
      );
      const below =
        rect.top - popupHeight - 8 < 12 &&
        rect.bottom + popupHeight + 8 <= window.innerHeight - 12;
      setStandalonePlacement((current) =>
        current.left === left &&
        current.width === width &&
        current.below === below
          ? current
          : { left, width, below },
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [standalone, rows]);
  const style = standalone
    ? {
        width: standalonePlacement.width,
        left: standalonePlacement.left,
        ...(standalonePlacement.below
          ? { top: "calc(100% + 8px)" }
          : { bottom: "calc(100% + 8px)" }),
      }
    : defaultStyle;

  useEffect(() => {
    setHighlightedIndex(0);
  }, [trigger.query, targets.rows]);

  async function pickRow(row: InputTargetConversation) {
    if (pickingId !== null) return;
    onBeforePick?.();
    if (await pick(row)) onPicked(trigger, row);
  }

  async function pickNew() {
    if (pickingId !== null || newPickPendingRef.current) return;
    onBeforePick?.();
    if (!onNewConversation) {
      requestStandaloneNewChat();
      onPicked(trigger, null);
      return;
    }
    newPickPendingRef.current = true;
    setPickingNew(true);
    try {
      if ((await onNewConversation()) !== false) onPicked(trigger, null);
    } catch {
      // Conversation creation reports its error; preserve the draft for retry.
    } finally {
      newPickPendingRef.current = false;
      setPickingNew(false);
    }
  }

  useImperativeHandle(ref, () => ({
    moveHighlight: (delta) =>
      setHighlightedIndex((index) =>
        Math.max(0, Math.min(index + delta, rowCount - 1)),
      ),
    pickHighlighted: () => {
      // Rows shown while a newer query loads may be stale: Enter waits.
      if (showNew && selectedIndex === 0) {
        void pickNew();
        return;
      }
      const row = rows[selectedIndex - (showNew ? 1 : 0)];
      if (row && targets.status === "ready") void pickRow(row);
    },
    pickNew: () => {
      if (!showNew) return false;
      void pickNew();
      return true;
    },
  }));

  const popup = (
    <InputTargetPickerSurface
      ref={popupRef}
      style={style}
      rows={rows}
      showNew={showNew}
      selectedIndex={selectedIndex}
      standalone={standalone}
      local={local}
      popoverScope={popoverScope}
      disabled={pickingId !== null || pickingNew}
      status={targets.status}
      mode={targets.mode}
      error={error}
      onPickNew={() => void pickNew()}
      onPickRow={(row) => void pickRow(row)}
    />
  );
  // The tray can switch to CSS glass. Its popup must retain its own material
  // above that containing surface.
  return tray ? createPortal(popup, document.body) : popup;
}
